import classnames from 'classnames'
import React, { useContext, memo, useEffect, Suspense, useState, lazy } from 'react'
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'

// Internal imports
import { useDialog } from 'components/common/modal/Modal'
// import SiteDownRoute from 'config/routes/siteDownRoute'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import { ScreenContext } from 'context/ScreenContext'
import { TermsContext } from 'context/TermsContext'
import { trackErrorScreen, trackCateringCartAbandonmentRateCartAbandoned } from 'middleware/helper'
import { appLaunch } from 'modules/authentication/actions'
import { displayAlcoholRestriction } from 'modules/location/actions'
import { setAgeVerificationRequired } from 'modules/user/actions'
import { setCateringMode } from 'modules/appSettings/actions'
import { util } from 'utils'
import useIdleRefresh from 'utils/idleRefesh'
import './scss/index.scss'
import { getCDNURL } from 'network/api/networkSettings'
import { useNavigate } from 'react-router-dom'
import { setNavigate } from 'config/redux'
import Footer from 'components/common/footer'

const Account = lazy(() =>
    import('./components/account').then(module => ({ default: module.Account }))
)
const AccountFooter = lazy(() =>
    import('./components/account').then(module => ({ default: module.AccountFooter }))
)
const AgeVerify = lazy(() => import('./components/ageVerify'))
const AlcoholRescriction = lazy(() => import('./components/common/alcoholRestriction'))
const Button = lazy(() => import('./components/common/button'))
const Cart = lazy(() => import('./components/cart').then(module => ({ default: module.Cart })))
const Drawer = lazy(() => import('./components/common/drawer'))
const ErrorScreen = lazy(() => import('./components/common/errorScreen'))
const Container = lazy(() =>
    import('./components/common/modal/Modal').then(module => ({ default: module.Container }))
)
const RateOrder = lazy(() => import('./components/common/RateOrder'))
const Orders = lazy(() => import('./components/orders'))
const MobileAccount = lazy(() => import('./components/signIn/MobileAccount'))
const FailedPaymentModal = lazy(() => import('./components/subscription/FailedPaymentModal'))
const TnpUpdate = lazy(() => import('./components/tnpUpdate'))

export const App = memo(() => {
    // Contexts
    const { rtl, strings } = useContext(LanguageContext)

    const dispatch = useDispatch()
    const location = useLocation()
    const { accountPanel, closePanel, basketPanel, panelOpen, signInPanel, orderPanel, openPanel } =
        useContext(LayoutContext)
    const { mobile, updateMedia } = useContext(ScreenContext)
    const { showModal } = useContext(TermsContext)

    //RtL Support
    if (rtl) document.dir = 'rtl'
    else document.dir = 'ltr'

    // Redux States
    const feedback_orderId = useSelector(state => state.orders.get('feedback_orderId'))
    const [state_feedback_orderId, setFeedbackOrderId] = useState(feedback_orderId)
    const [state_show_alcoholRestriction, setAlcoholRestriction] = useState(false)
    const showAlcoholSalesHour = useSelector(state => state.location.toJS().showAlcoholSalesHour)

    const displayTermsModal = useSelector(state => state.authentication.get('displayTermsModal'))
    const displayPrivacyModal = useSelector(state =>
        state.authentication.get('displayPrivacyModal')
    )
    const [state_displayTnPModal, setDisplayTnPModal] = useState(false)
    const tnpUpadteFailed = useSelector(state => state.authentication.get('tnpUpadteFailed'))
    const [state_tnpUpadteFailed, setTnpUpadteFailed] = useState(tnpUpadteFailed)
    const tnpUpdateSuccess = useSelector(state => state.authentication.get('tnpUpdateSuccess'))
    const siteDownFlag = useSelector(state => state.configuration.getIn(['7NOW_Web_Offline']))
    const [state_siteDownFlag, setSiteDownFlag] = useState(siteDownFlag)
    const ageVerification = util.ageVerificationNeeded()
    const showAgeVerification = useSelector(state => state.user.getIn(['needAgeVerification']))
    const isFromRewards = useSelector(state => state.appSettings.get('isApp'))
    const screenError = useSelector(state => state.tokenRefresh.get('screenError'))
    const errorDescription = useSelector(state => state.tokenRefresh.get('screenErrorDescription'))
    const rewardsWebViewLaunched = useSelector(state =>
        state.authentication.get('MobileBridgeAppLaunchSuccess')
    )
    const userProfile = useSelector(state => state.user.get('userProfile'))?.toJS()
    const showFailedPaymentModal = useSelector(
        state => state.subscription.showFailedSubscriptionPaymentModal
    )
    const basketItems = useSelector(state => state.basket.get('basketItems'))
    const [tncArr, setTncArr] = useState([])

    const navigate = useNavigate()

    useEffect(() => {
        setNavigate(navigate)
    }, [navigate])
    
    useEffect(() => {
        const path = location.pathname
        const isOnCateringRoute = path.indexOf('/catering') === 0
        const isOnShopRoute = path.indexOf('/shop') === 0 || path.indexOf('/home') === 0
        
        // Only update catering mode when on explicit catering or shop/home routes
        // Preserve current mode on neutral pages like /checkout, /order-status, /orders, etc.
        if (isOnCateringRoute) {
            dispatch(setCateringMode(true))
            // Force pickup mode for catering
            import('modules/location/actions').then(({ setChoiceButton }) => {
                import('utils/appUtils').then(({ orderTypes }) => {
                    dispatch(setChoiceButton(orderTypes.PICKUP))
                })
            })
        } else if (isOnShopRoute) {
            dispatch(setCateringMode(false))
        }
    }, [location.pathname, dispatch])

    // custom hook for 24hr idle refresh
    useIdleRefresh()

    useEffect(() => {
        screenError && trackErrorScreen(errorDescription)
    }, [screenError, errorDescription])
    
    // Global cart abandonment tracking for catering
    useEffect(() => {
        const handleBeforeUnload = () => {
            const items = basketItems?.toJS ? basketItems.toJS() : (basketItems || [])
            const hasCateringItems = items?.some(item => item?.platter_item_flag === '1')
            
            if (hasCateringItems) {
                const itemsCount = items?.reduce((sum, item) => sum + (item.count || 0), 0) || 0
                const cartValue = items?.reduce((sum, item) => sum + ((item.price || 0) * (item.count || 1)), 0) || 0
                
                trackCateringCartAbandonmentRateCartAbandoned(
                    itemsCount,
                    cartValue / 100,
                    0,
                    'tab_closed'
                )
            }
        }
        
        window.addEventListener('beforeunload', handleBeforeUnload)
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [basketItems])

    useEffect(() => {
        if (tnpUpdateSuccess) {
            setDisplayTnPModal(false)
            hide()
        } else if (tnpUpadteFailed) {
            setTnpUpadteFailed(tnpUpadteFailed)
            show()
        } else if (showModal && userProfile?.required_terms?.length) {
            let tncRequired = []
            for (let element of userProfile.required_terms) {
                if (element.type === 'privacy' || element.type === 'terms') {
                    tncRequired.push(element)
                }
            }
            if (tncRequired.length !== tncArr.length) {
                setTncArr(tncRequired)
            }
            setDisplayTnPModal(true)
            show()
        } else if ((displayTermsModal || displayPrivacyModal) && !state_displayTnPModal) {
            setDisplayTnPModal(displayTermsModal || displayPrivacyModal)
            show()
        } else if (!(displayTermsModal || displayPrivacyModal) && state_displayTnPModal) {
            setDisplayTnPModal(displayTermsModal || displayPrivacyModal)
        } else if (ageVerification.isNeeded && showAgeVerification) {
            show()
        }
    }, [
        displayTermsModal,
        displayPrivacyModal,
        tnpUpadteFailed,
        tnpUpdateSuccess,
        showAgeVerification,
        userProfile,
    ])

    useEffect(() => {
        //TODO do we need it here as user won't manually refresh
        if (!window.location.search.includes('app=true') || rewardsWebViewLaunched) {
            dispatch(appLaunch())
        }
        window.addEventListener('resize', updateMedia)
        return () => window.removeEventListener('resize', updateMedia)
    }, [])

    useEffect(() => {
        if (!state_feedback_orderId && feedback_orderId) {
            setFeedbackOrderId(feedback_orderId)
            show()
        } else if (state_feedback_orderId && !feedback_orderId) {
            setFeedbackOrderId(null)
        }
    }, [feedback_orderId])

    useEffect(() => {
        if (!state_show_alcoholRestriction && showAlcoholSalesHour) {
            setAlcoholRestriction(showAlcoholSalesHour)
            dispatch(displayAlcoholRestriction(false))
            show()
        }
    }, [showAlcoholSalesHour])

    useEffect(() => {
        const shopOrHome = location.pathname === '/shop' || location.pathname === '/home'
        if (showFailedPaymentModal && shopOrHome) {
            show()
        }
    }, [showFailedPaymentModal])

    useEffect(() => {
        if (siteDownFlag && !state_siteDownFlag) {
            setSiteDownFlag(true)
        } else if (!siteDownFlag && state_siteDownFlag) {
            setSiteDownFlag(false)
        }
    }, [siteDownFlag])

    useEffect(() => {
        if (mobile && location.pathname === '/user-account') {
            openPanel('accountPanel')
        }
    }, [location])

    const cartOpenClass = () =>
        mobile ? 'app-container__move-left-mobile' : 'app-container__move-left'
    const { on, hide, show, getToggleProps, getContainerProps } = useDialog()

    const getDrawerClassName = () =>
        classnames({
            'basket-panel': basketPanel,
            'sign-in-panel': signInPanel,
            'order-panel': orderPanel,
            'account-panel': accountPanel,
        })

    const getTitle = () => {
        switch (true) {
            case signInPanel:
                return strings.accountLabel

            case orderPanel:
                return strings.Orders

            case accountPanel:
                return strings.accountLabel

            //TODO get basket count from util not from basketItems in reducers
            case basketPanel:
            default:
                return strings.BASKET_TITLE
        }
    }
    const getContent = () => {
        switch (true) {
            case basketPanel:
                return (
                    <Suspense fallback={<div></div>}>
                        <Cart panelOpen={panelOpen} />
                    </Suspense>
                )
            case signInPanel:
                return (
                    <Suspense fallback={<div></div>}>
                        <MobileAccount />
                    </Suspense>
                )

            case orderPanel:
                return (
                    <Suspense fallback={<div></div>}>
                        <Orders />
                    </Suspense>
                )
            case accountPanel:
                return (
                    <Suspense fallback={<div></div>}>
                        <Account />
                    </Suspense>
                )
            default:
                return (
                    <Suspense fallback={<div></div>}>
                        <Cart panelOpen={panelOpen} />
                    </Suspense>
                )
        }
    }

    const getFooter = () => {
        switch (true) {
            case accountPanel && !isFromRewards:
                return (
                    <Suspense fallback={<div></div>}>
                        <AccountFooter />
                    </Suspense>
                )
            default:
                return null
        }
    }

    const handleAgeVerifyClick = () => {
        dispatch(setAgeVerificationRequired(false))
    }

    return (
        <div style={{ display: 'flex', minWidth: '100%' }}>
            {screenError && mobile ? (
                <Suspense fallback={<div></div>}>
                    <ErrorScreen />
                </Suspense>
            ) : (
                <>
                    <div
                        className={`app-container ${panelOpen ? cartOpenClass() : ''}`}
                        onClick={panelOpen ? () => closePanel() : null}
                    >
                        <Suspense
                            fallback={
                                <div className={'appLoad'}>
                                    <img
                                        className={'appLoad-ing'}
                                        src={getCDNURL() + 'assets/gifs/7now_app.gif'}
                                        alt={strings.loading}
                                    />
                                </div>
                            }
                        >
                            {/* {siteDownFlag ? SiteDownRoute : null} */}
                            <Outlet />
                            <Footer />
                        </Suspense>
                        <>
                            {on && state_show_alcoholRestriction && (
                                <Suspense fallback={<div></div>}>
                                    <Container {...getContainerProps()}>
                                        <div className={'registerform'}>
                                            <Button
                                                {...getToggleProps()}
                                                aria-label="close modal"
                                                className="closeModal"
                                            >
                                                <AiOutlineClose className={'close'} size="2.5rem" />
                                            </Button>
                                            <AlcoholRescriction
                                                hide={hide}
                                                setAlcoholRestriction={setAlcoholRestriction}
                                            />
                                        </div>
                                    </Container>
                                </Suspense>
                            )}
                        </>
                        <>
                            {on &&
                                state_feedback_orderId &&
                                !state_displayTnPModal &&
                                !state_show_alcoholRestriction && (
                                    <Suspense fallback={<div></div>}>
                                        <Container {...getContainerProps()}>
                                            <div className={'rateOrderform'}>
                                                <Button
                                                    {...getToggleProps()}
                                                    aria-label="close modal"
                                                    className="closeModal"
                                                >
                                                    <AiOutlineClose
                                                        className={'close'}
                                                        size="2.5rem"
                                                    />
                                                </Button>
                                                <RateOrder
                                                    hide={hide}
                                                    orderId={state_feedback_orderId}
                                                />
                                            </div>
                                        </Container>
                                    </Suspense>
                                )}
                        </>
                        <>
                            {on && showAgeVerification && (
                                <Suspense fallback={<div></div>}>
                                    <Container {...getContainerProps()}>
                                        <div className={'signinform'}>
                                            <Button
                                                {...getToggleProps()}
                                                aria-label="close modal"
                                                className="closeModal"
                                            >
                                                <AiOutlineClose
                                                    className={'close'}
                                                    color="#00805F"
                                                    size="2.5rem"
                                                    onClick={handleAgeVerifyClick}
                                                />
                                            </Button>
                                            <AgeVerify hide={hide} />
                                        </div>
                                    </Container>
                                </Suspense>
                            )}
                        </>
                        <>
                            {on && state_displayTnPModal && (
                                <Suspense fallback={<div></div>}>
                                    <Container>
                                        <div className={'registerform'}>
                                            <TnpUpdate
                                                hide={hide}
                                                tnpUpadteFailed={state_tnpUpadteFailed}
                                                tncArr={tncArr}
                                            />
                                        </div>
                                    </Container>
                                </Suspense>
                            )}
                        </>
                        <>
                            {on && showFailedPaymentModal && (
                                <Suspense fallback={<div></div>}>
                                    <Container {...getContainerProps()}>
                                        <div className="failedPaymentModal">
                                            <Button
                                                {...getToggleProps()}
                                                aria-label="close modal"
                                                className="closeModal"
                                            >
                                                <AiOutlineClose className={'close'} size="2.5rem" />
                                            </Button>
                                            <FailedPaymentModal hide={hide} />
                                        </div>
                                    </Container>
                                </Suspense>
                            )}
                        </>
                    </div>

                    {panelOpen ? (
                        <Suspense fallback={<div></div>}>
                            <Drawer
                                title={getTitle()}
                                footer={getFooter()}
                                className={getDrawerClassName()}
                            >
                                {getContent()}
                            </Drawer>
                        </Suspense>
                    ) : null}
                </>
            )}
        </div>
    )
})
